<template>
  <Dialog v-model="dialogVisible" title="同意售后" width="520px">
    <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="formRules" label-width="120px">
      <template v-if="isExchange">
        <el-alert
          type="info"
          :closable="false"
          title="换货不涉及退款"
          description="同意后买家寄回原件，您确认收货后发出同规格换货。"
          class="mb-12px"
        />
      </template>
      <template v-else>
        <el-form-item label="申请退款金额">
          <span>{{ fenToYuan(applyRefundPrice) }} 元</span>
        </el-form-item>
        <el-form-item v-if="itemPayPrice != null" label="商品实付">
          <span>{{ fenToYuan(itemPayPrice) }} 元</span>
        </el-form-item>
        <el-form-item v-if="usePoint" label="退还积分">
          <span>{{ usePoint }} 分</span>
          <span class="ml-8px text-12px text-gray-400">确认退款后自动退回买家</span>
        </el-form-item>
        <el-form-item v-if="pointPrice" label="积分抵扣">
          <span>{{ fenToYuan(pointPrice) }} 元</span>
        </el-form-item>
        <el-form-item label="同意退款金额" prop="refundPriceYuan">
          <el-input-number
            v-model="formData.refundPriceYuan"
            :min="0.01"
            :max="maxRefundYuan"
            :precision="2"
            :step="0.01"
            class="!w-220px"
          />
          <span class="ml-8px text-gray-400">元</span>
        </el-form-item>
        <el-form-item>
          <span class="text-13px text-gray-500">可下调为部分退款，不可高于申请金额</span>
        </el-form-item>
      </template>
      <el-form-item v-if="needReturnAddress" label="退货地址">
        <div v-if="returnAddressReady" class="text-13px leading-22px">
          <div>{{ returnPreview.returnName }} {{ returnPreview.returnMobile }}</div>
          <div class="text-gray-500">{{ returnAddressLine }}</div>
          <div class="mt-4px text-12px text-gray-400">同意后将下发给买家，请确认店铺资料中的地址正确</div>
        </div>
        <el-alert
          v-else
          type="warning"
          :closable="false"
          title="尚未配置店铺退货地址"
          description="请先在店铺资料中完善退货收件人、手机、地区与详细地址，再同意换货/退货退款。"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button
        :disabled="formLoading || (needReturnAddress && !returnAddressReady)"
        type="primary"
        @click="submitForm"
      >
        确 定
      </el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as AfterSaleApi from '@/api/mall/trade/afterSale/index'
import { getShop } from '@/api/merchant/shop-manager'
import { getAreaTree } from '@/api/system/area'
import { formatReturnAddressLine } from '@/utils/areaFormat'
import { fenToYuan, fenToYuanNumber, yuanToFen } from '@/utils'

defineOptions({ name: 'AfterSaleAgreeForm' })

const { t } = useI18n()
const message = useMessage()

const dialogVisible = ref(false)
const formLoading = ref(false)
const applyRefundPrice = ref(0)
const itemPayPrice = ref<number | null>(null)
const usePoint = ref(0)
const pointPrice = ref(0)
const afterSaleWay = ref<number | null>(null)
const areaTree = ref<any[]>([])
const returnPreview = ref<{
  returnName?: string
  returnMobile?: string
  returnAreaId?: number
  returnDetailAddress?: string
}>({})
const formData = ref<{
  id: number | null | undefined
  refundPriceYuan: number | undefined
}>({
  id: undefined,
  refundPriceYuan: undefined
})
const formRef = ref()

const isExchange = computed(() => afterSaleWay.value === 30)
const needReturnAddress = computed(() => afterSaleWay.value === 20 || afterSaleWay.value === 30)
const returnAddressReady = computed(
  () =>
    !!returnPreview.value.returnName?.trim() &&
    !!returnPreview.value.returnMobile?.trim() &&
    returnPreview.value.returnAreaId != null &&
    !!returnPreview.value.returnDetailAddress?.trim()
)
const returnAddressLine = computed(() =>
  formatReturnAddressLine(areaTree.value, returnPreview.value)
)

const maxRefundYuan = computed(() => {
  const applyYuan = fenToYuanNumber(applyRefundPrice.value)
  if (itemPayPrice.value == null) {
    return applyYuan
  }
  return Math.min(applyYuan, fenToYuanNumber(itemPayPrice.value))
})

const formRules = reactive({
  refundPriceYuan: [
    { required: true, message: '请输入同意退款金额', trigger: 'blur' },
    {
      validator: (_rule: any, value: number, callback: (e?: Error) => void) => {
        if (value == null || value <= 0) {
          callback(new Error('退款金额必须大于 0'))
          return
        }
        if (value > maxRefundYuan.value) {
          callback(new Error('退款金额不能高于申请金额'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ]
})

/** 打开弹窗 */
const open = async (row: AfterSaleApi.TradeAfterSaleDetailVO | AfterSaleApi.TradeAfterSaleVO) => {
  resetForm()
  formData.value.id = row.id
  applyRefundPrice.value = row.refundPrice || 0
  afterSaleWay.value = row.way ?? null
  const detail = row as AfterSaleApi.TradeAfterSaleDetailVO
  itemPayPrice.value = detail.orderItem?.payPrice ?? null
  usePoint.value = detail.usePoint || detail.orderItem?.usePoint || 0
  pointPrice.value = detail.pointPrice || detail.orderItem?.pointPrice || 0
  formData.value.refundPriceYuan = fenToYuanNumber(applyRefundPrice.value)
  if (needReturnAddress.value) {
    const shopId = detail.order?.shopId ?? row.shopId
    if (!areaTree.value.length) {
      areaTree.value = (await getAreaTree()) || []
    }
    if (shopId) {
      try {
        const shop = await getShop(Number(shopId))
        returnPreview.value = {
          returnName: shop?.returnName,
          returnMobile: shop?.returnMobile,
          returnAreaId: shop?.returnAreaId,
          returnDetailAddress: shop?.returnDetailAddress
        }
      } catch {
        returnPreview.value = {}
      }
    }
  }
  dialogVisible.value = true
}
defineExpose({ open })

const emit = defineEmits(['success'])
const submitForm = async () => {
  if (needReturnAddress.value && !returnAddressReady.value) {
    message.warning('请先在店铺资料中配置退货地址')
    return
  }
  if (!isExchange.value) {
    await formRef.value?.validate()
  }
  formLoading.value = true
  try {
    await AfterSaleApi.agree({
      id: formData.value.id!,
      refundPrice: isExchange.value ? 0 : yuanToFen(formData.value.refundPriceYuan!)
    })
    message.success(t('common.updateSuccess'))
    dialogVisible.value = false
    emit('success', true)
  } finally {
    formLoading.value = false
  }
}

const resetForm = () => {
  formData.value = {
    id: undefined,
    refundPriceYuan: undefined
  }
  applyRefundPrice.value = 0
  itemPayPrice.value = null
  usePoint.value = 0
  pointPrice.value = 0
  afterSaleWay.value = null
  returnPreview.value = {}
  formRef.value?.resetFields()
}
</script>
